import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { supabase } from "../supabaseClient";

export default function Login() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!formData.email || !formData.password) {
            return;
        }

        setIsLoading(true);
        setIsSuccess(false);
        setErrorMsg("");

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email: formData.email,
                password: formData.password,
            });

            if (error) throw error;

            console.log("Login successful:", data);
            setIsSuccess(true);
            
            // Redirect after 1.5 seconds to let success animation finish
            setTimeout(() => {
                navigate("/dashboard");
            }, 1500);
        } catch (error) {
            console.error("Login error:", error.message);
            setErrorMsg(error.message || "Invalid email or password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex justify-center items-center py-20 px-4">
            <SEO
                title="Login to Your Account"
                description="Sign in to your blinqbooks account to manage your accounting, invoicing, payroll, and business operations."
            />
            <div
                className={`bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md transition-all duration-500 ${isLoading ? 'scale-105 shadow-xl' : ''} ${isSuccess ? 'scale-110 shadow-glow' : ''}`}>
                <div className="text-center mb-8">
                    <h1 className={`text-3xl font-bold text-primary dark:text-white mb-2`}>
                        {isSuccess ? 'Welcome Back!' : 'Login to blinqbooks'}
                    </h1>
                    <p className="text-secondary dark:text-gray-400">Enter your credentials to access your account</p>
                </div>

                {errorMsg && (
                    <div className="p-3 mb-6 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm text-center font-medium">
                        {errorMsg}
                    </div>
                )}

                <div className="space-y-6">
                    <div className={`transition-all duration-300 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                        <label className="block text-sm font-medium mb-1 text-primary dark:text-gray-300">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={isLoading}
                            className="w-full px-4 py-3 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-500/5 dark:bg-white/5 focus:ring-2 focus:ring-accent outline-none transition-all"
                            placeholder="name@company.com"
                        />
                    </div>
                    <div className={`transition-all duration-300 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                        <label className="block text-sm font-medium mb-1 text-primary dark:text-gray-300">Password</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            disabled={isLoading}
                            className="w-full px-4 py-3 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-500/5 dark:bg-white/5 focus:ring-2 focus:ring-accent outline-none transition-all"
                            placeholder="••••••••"
                        />
                    </div>
                    <div className="text-right mt-1">
                        <Link to="/forgot-password" className="text-xs text-accent hover:underline font-medium transition-colors">
                            Forgot password?
                        </Link>
                    </div>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading || !formData.email || !formData.password}
                        className={`w-full py-3 font-semibold text-white rounded-full transition-all duration-300 transform active:scale-95 ${isSuccess
                            ? 'bg-green-500 shadow-green-200'
                            : isLoading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-accent hover:bg-accent-hover'
                            }`}
                    >
                        {isSuccess ? (
                            <span className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd" />
                                </svg>
                                <span>Success!</span>
                            </span>
                        ) : isLoading ? (
                            <span className="flex items-center justify-center space-x-2">
                                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor"
                                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Logging in...</span>
                            </span>
                        ) : (
                            'Login'
                        )}
                    </button>
                    <div className="text-center mt-8">
                        <p className="text-sm text-secondary dark:text-gray-400">
                            Don't have an account?{' '}
                            <Link to="/signup" className="text-accent font-bold hover:underline">Sign up</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}